export interface ArisConfig {
  /** Project slug as configured in the collector. */
  projectId: string;
  environment: string;
  /** Full ingest URL, e.g. https://watcher.arisweb.ru/api/events/ingest */
  collectorUrl: string;
  apiKey: string;

  /** Show the bottom-right dev widget. Default: false (recommend dev only). */
  showWidget?: boolean;

  /** Domains considered "planned". Glob-ish: exact host or suffix match. */
  allowedDomains?: string[];
  blockedDomains?: string[];
  /** If true, requests to unknown domains are flagged hard (and visually). */
  blockUnknownDomains?: boolean;
  /**
   * Inline firewall: actually *block* (reject before sending), not just flag, requests
   * that hit `blockedDomains` or — when `blockUnknownDomains` is set — an unknown
   * cross-origin domain. Blocked requests are still recorded (tag `firewall_blocked`).
   * Default false (flag-only, backward compatible).
   */
  enforce?: boolean;

  /** 0..1 sampling. Errors are always sampled regardless. Default 1. */
  sampleRate?: number;
  alwaysSampleErrors?: boolean;

  /** Capture global JS errors + unhandled rejections (Sentry-style). Default true. */
  captureErrors?: boolean;

  /** Capture Web Vitals (LCP/CLS/INP/FCP/TTFB) + page-load timing. Default true. */
  captureWebVitals?: boolean;

  /** Capture main-thread Long Tasks (web analog of ANR). Default true. */
  captureLongTasks?: boolean;
  /** Only long tasks at least this long are reported. Default 200 (ms). */
  longTaskMinMs?: number;

  /** Sample JS heap usage (Chrome performance.memory) for leak detection. Default true. */
  captureMemory?: boolean;
  /** Memory sampling period. Default 60000 (ms). */
  memorySampleMs?: number;

  /**
   * Production profiler (JS Self-Profiling API, Chromium; the page must be served with
   * `Document-Policy: js-profiling`). Share of page loads to profile, 0..1. Default 0 (off).
   */
  profilerSampleRate?: number;

  /** Also mask emails/phones in URLs and previews. Default false. */
  maskPii?: boolean;
  /** Capture a short masked body preview (request only). Default false. */
  captureBodyPreview?: boolean;

  /** Batching/transport tuning. */
  batchSize?: number;       // flush when buffer reaches this many. Default 20.
  flushIntervalMs?: number; // periodic flush. Default 5000.
  maxQueueSize?: number;    // offline cap (events). Default 500.

  /** Stable identifiers for correlation. */
  userId?: string | (() => string | undefined);
  sessionId?: string;

  /** Don't intercept these (substring match on URL). The collector URL is auto-excluded. */
  ignoreUrls?: (string | RegExp)[];

  /** Static tags attached to every event (e.g. { release: '1.4.0' }). */
  tags?: Record<string, unknown>;

  debug?: boolean;
}

export interface ResolvedConfig extends Required<Omit<ArisConfig,
  'userId' | 'sessionId' | 'ignoreUrls' | 'tags' | 'allowedDomains' | 'blockedDomains'>> {
  allowedDomains: string[];
  blockedDomains: string[];
  ignoreUrls: (string | RegExp)[];
  tags: Record<string, unknown>;
  userId?: string | (() => string | undefined);
  sessionId?: string;
}

export function resolveConfig(cfg: ArisConfig): ResolvedConfig {
  return {
    projectId: cfg.projectId,
    environment: cfg.environment,
    collectorUrl: cfg.collectorUrl,
    apiKey: cfg.apiKey,
    showWidget: cfg.showWidget ?? false,
    allowedDomains: cfg.allowedDomains ?? [],
    blockedDomains: cfg.blockedDomains ?? [],
    blockUnknownDomains: cfg.blockUnknownDomains ?? false,
    enforce: cfg.enforce ?? false,
    captureErrors: cfg.captureErrors ?? true,
    captureWebVitals: cfg.captureWebVitals ?? true,
    captureLongTasks: cfg.captureLongTasks ?? true,
    longTaskMinMs: cfg.longTaskMinMs ?? 200,
    captureMemory: cfg.captureMemory ?? true,
    memorySampleMs: cfg.memorySampleMs ?? 60_000,
    profilerSampleRate: cfg.profilerSampleRate ?? 0,
    sampleRate: cfg.sampleRate ?? 1,
    alwaysSampleErrors: cfg.alwaysSampleErrors ?? true,
    maskPii: cfg.maskPii ?? false,
    captureBodyPreview: cfg.captureBodyPreview ?? false,
    batchSize: cfg.batchSize ?? 20,
    flushIntervalMs: cfg.flushIntervalMs ?? 5000,
    maxQueueSize: cfg.maxQueueSize ?? 500,
    ignoreUrls: cfg.ignoreUrls ?? [],
    tags: cfg.tags ?? {},
    userId: cfg.userId,
    sessionId: cfg.sessionId,
    debug: cfg.debug ?? false,
  };
}
